/**
 * Whether a piece of text is a password rather than words: what Bridge shows on a password plate (the Passwork key,
 * dots, an eye) instead of as text. A password is one line with no spaces, 6 to 64 characters, not a link or an email
 * address, and mixes kinds of character — lower case, upper case, digits, symbols: three of them including digits,
 * or all four, or for long tokens (16 characters or more) digits and one other kind. Words with a comma or an
 * apostrophe (“Hello,”, “Don't”) have no digits and stay text.
 */
const LINK = /^(?:[a-z][a-z0-9+.-]*:\/\/|www\.)/i;
const EMAIL = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;

export function looksLikePassword(text: string): boolean {
  if (text.length < 6 || text.length > 64 || /\s/.test(text)) return false;
  if (LINK.test(text) || EMAIL.test(text)) return false;
  const kinds = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((kind) => kind.test(text)).length;
  const digits = /[0-9]/.test(text);
  return kinds === 4 || (kinds === 3 && digits) || (kinds >= 2 && digits && text.length >= 16);
}

/**
 * A shared text as a password and a note: the first line when it is a password, and whatever follows it. The
 * composer sends a password and its note this way, as one encrypted text.
 */
export function splitPassword(text: string): { readonly password: string; readonly note: string } | null {
  const end = text.indexOf('\n');
  const head = end === -1 ? text : text.slice(0, end);
  if (!looksLikePassword(head)) return null;
  return { password: head, note: end === -1 ? '' : text.slice(end + 1).trim() };
}
